import { useEffect } from "react"
import { useForm, type Resolver } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { useQuery, useMutation } from "urql"
import { toast } from "sonner"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Switch } from "@/components/ui/switch"
import { Loader2 } from "lucide-react"
import { FieldError } from "@/components/shared/field-error"
import { DestinationTypeSelector, DestinationTypeConfigFields } from "@/components/shared/destination-type-fields"
import { endpointSchema, type EndpointFormValues, type DestinationTypeValue, outputFormatLabels, outputFormatValues, type OutputFormatValue } from "@/lib/schemas"
import { buildTypeConfig, extractTypeConfigFields } from "@/lib/type-config"
import {
  ApplicationsQuery,
  CreateEndpointMutation,
  UpdateEndpointMutation,
} from "@/lib/graphql/outbound"
import { TransformationsQuery } from "@/lib/graphql/queries"
import type { ResultOf } from "@/lib/graphql/types"
import type { EndpointsQuery } from "@/lib/graphql/outbound"

type EndpointItem = ResultOf<typeof EndpointsQuery>["endpoints"]["nodes"][number] & {
  filterConfig?: { eventTypes?: string[] | null } | null
  headers?: Record<string, unknown> | null
  retryPolicy?: { maxAttempts: number; initialDelay: string; maxDelay: string; backoffFactor: number } | null
  oauth2Config?: { tokenUrl: string; clientId: string; clientSecret: string; scopes?: string | null; audience?: string | null } | null
  mtlsCert?: string | null
  mtlsKey?: string | null
  typeConfig?: Record<string, unknown> | null
  healthConfig?: { windowHours: number; disableBelow: number } | null
  outputFormat?: string | null
  transformationId?: string | null
  connectorType?: string
  autoDeletePayload?: boolean
  delay?: string
  channels?: string[]
}

interface EndpointFormProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess: () => void
  initialData?: EndpointItem
}

export function EndpointForm({ open, onOpenChange, onSuccess, initialData }: EndpointFormProps) {
  const isEdit = !!initialData
  const [, create] = useMutation(CreateEndpointMutation)
  const [, update] = useMutation(UpdateEndpointMutation)
  const [apps] = useQuery({ query: ApplicationsQuery, variables: { limit: 100 } })
  const [transformations] = useQuery({ query: TransformationsQuery })

  const form = useForm<EndpointFormValues>({
    resolver: zodResolver(endpointSchema) as Resolver<EndpointFormValues>,
    defaultValues: buildDefaults(initialData),
  })

  const authType = form.watch("authType")
  const deliveryMode = form.watch("deliveryMode")
  const destType = (form.watch("type") ?? "HTTP") as DestinationTypeValue
  const isPoll = deliveryMode === "POLL"
  const isHttp = destType === "HTTP"
  const connectorType = form.watch("connectorType")

  useEffect(() => {
    if (open) {
      form.reset(buildDefaults(initialData))
    }
  }, [open, initialData, form])

  const onSubmit = async (values: EndpointFormValues) => {
    let headers: Record<string, unknown> | undefined
    if (values.headers) {
      try {
        headers = JSON.parse(values.headers) as Record<string, unknown>
      } catch {
        form.setError("headers", { message: "Invalid JSON" })
        return
      }
    }

    const filterConfig = values.eventTypes
      ? { eventTypes: values.eventTypes.split(",").map((s) => s.trim()).filter(Boolean) }
      : undefined

    const authFields = {
      authType: values.authType ?? "NONE",
      oauth2Config: values.authType === "OAUTH2" ? values.oauth2Config : null,
      mtlsCert: values.authType === "MTLS" ? (values.mtlsCert || null) : null,
      mtlsKey: values.authType === "MTLS" ? (values.mtlsKey || null) : null,
    }

    const currentType = (values.type ?? "HTTP") as DestinationTypeValue
    const mode = values.deliveryMode ?? "PUSH"
    const urlValue = currentType !== "HTTP" ? undefined : (mode === "POLL" ? undefined : values.url)
    const typeConfig = buildTypeConfig(currentType, values)

    const healthConfig = values.healthConfig?.windowHours && values.healthConfig?.disableBelow !== undefined
      ? { windowHours: values.healthConfig.windowHours, disableBelow: values.healthConfig.disableBelow }
      : undefined

    const delay = values.delay || undefined
    const channels = values.channels ? values.channels.split(",").map((s) => s.trim()).filter(Boolean) : undefined
    const transformationId = values.transformationId || undefined

    if (isEdit) {
      const result = await update({
        id: initialData.id,
        input: {
          type: currentType,
          typeConfig: typeConfig ?? null,
          url: urlValue,
          deliveryMode: currentType === "HTTP" ? (mode as "PUSH" | "POLL") : undefined,
          timeoutMs: currentType === "HTTP" && mode !== "POLL" ? values.timeoutMs : undefined,
          rateLimitRps: values.rateLimitRps,
          retryPolicy: currentType === "HTTP" && mode !== "POLL" ? values.retryPolicy : undefined,
          headers,
          filterConfig,
          status: values.status,
          ordered: values.ordered,
          outputFormat: values.outputFormat,
          healthConfig,
          transformationId: transformationId ?? null,
          connectorType: currentType === "CONNECTOR" ? (values.connectorType || undefined) : undefined,
          autoDeletePayload: values.autoDeletePayload,
          delay,
          channels,
          ...authFields,
        },
      })
      if (result.error) { toast.error(result.error.message); return }
      toast.success("Endpoint updated")
    } else {
      const result = await create({
        input: {
          applicationId: values.applicationId,
          type: currentType,
          typeConfig: typeConfig ?? null,
          url: urlValue,
          deliveryMode: currentType === "HTTP" ? (mode as "PUSH" | "POLL") : undefined,
          timeoutMs: currentType === "HTTP" && mode !== "POLL" ? values.timeoutMs : undefined,
          rateLimitRps: values.rateLimitRps,
          retryPolicy: currentType === "HTTP" && mode !== "POLL" ? values.retryPolicy : undefined,
          headers,
          filterConfig,
          ordered: values.ordered,
          outputFormat: values.outputFormat,
          healthConfig,
          transformationId,
          connectorType: currentType === "CONNECTOR" ? (values.connectorType || undefined) : undefined,
          autoDeletePayload: values.autoDeletePayload,
          delay,
          channels,
          ...authFields,
        },
      })
      if (result.error) { toast.error(result.error.message); return }
      toast.success("Endpoint created")
    }

    onSuccess()
    onOpenChange(false)
    form.reset()
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit Endpoint" : "Create Endpoint"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-4 rounded-lg glass-card p-4">
            {!isEdit && (
              <div className="space-y-2">
                <Label htmlFor="applicationId">Application</Label>
                <Select
                  value={form.watch("applicationId")}
                  onValueChange={(v) => form.setValue("applicationId", v, { shouldValidate: true })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select application" />
                  </SelectTrigger>
                  <SelectContent>
                    {apps.data?.applications.nodes.map((a) => (
                      <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FieldError error={form.formState.errors.applicationId} />
              </div>
            )}

            <div className="space-y-2">
              <Label>Status</Label>
              <Select
                value={form.watch("status") ?? "ACTIVE"}
                onValueChange={(v) => form.setValue("status", v as EndpointFormValues["status"])}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ACTIVE">Active</SelectItem>
                  <SelectItem value="INACTIVE">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <DestinationTypeSelector form={form} destType={destType} />

            {destType === "CONNECTOR" && (
              <div className="space-y-2">
                <Label>Connector Type</Label>
                <Select
                  value={form.watch("connectorType") ?? ""}
                  onValueChange={(v) => form.setValue("connectorType", v as EndpointFormValues["connectorType"])}
                >
                  <SelectTrigger><SelectValue placeholder="Select connector..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="slack">Slack</SelectItem>
                    <SelectItem value="discord">Discord</SelectItem>
                    <SelectItem value="teams">Microsoft Teams</SelectItem>
                    <SelectItem value="email">Email (SMTP)</SelectItem>
                  </SelectContent>
                </Select>
                <FieldError error={form.formState.errors.connectorType} />
              </div>
            )}

            {destType === "CONNECTOR" && connectorType && connectorType !== "email" && (
              <div className="space-y-2">
                <Label htmlFor="url">Webhook URL</Label>
                <Input id="url" className="font-mono text-sm" placeholder="https://hooks.slack.com/..." {...form.register("url")} />
                <FieldError error={form.formState.errors.url} />
              </div>
            )}

            {isHttp && (
              <div className="space-y-2">
                <Label htmlFor="deliveryMode">Delivery Mode</Label>
                <Select
                  value={deliveryMode ?? "PUSH"}
                  onValueChange={(v) => form.setValue("deliveryMode", v as "PUSH" | "POLL", { shouldValidate: true })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="PUSH">Push</SelectItem>
                    <SelectItem value="POLL">Poll</SelectItem>
                  </SelectContent>
                </Select>
                {isPoll && (
                  <p className="text-xs text-muted-foreground">
                    Poll mode: deliveries are queued and retrieved via the poll API instead of being pushed to a URL.
                  </p>
                )}
              </div>
            )}

            {isHttp && !isPoll && (
              <div className="space-y-2">
                <Label htmlFor="url">URL</Label>
                <Input id="url" className="font-mono text-sm" placeholder="https://..." {...form.register("url")} />
                <FieldError error={form.formState.errors.url} />
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              {isHttp && !isPoll && (
                <div className="space-y-2">
                  <Label htmlFor="timeoutMs">Timeout (ms)</Label>
                  <Input id="timeoutMs" type="number" {...form.register("timeoutMs")} />
                  <FieldError error={form.formState.errors.timeoutMs} />
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="rateLimitRps">Rate Limit (RPS)</Label>
                <Input id="rateLimitRps" type="number" {...form.register("rateLimitRps")} />
                <FieldError error={form.formState.errors.rateLimitRps} />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="eventTypes">Event Types (comma-separated)</Label>
              <Input id="eventTypes" placeholder="invoice.*, payment.completed" {...form.register("eventTypes")} />
            </div>

            <div className="space-y-2">
              <Label htmlFor="delay">Delay</Label>
              <Input id="delay" className="font-mono" placeholder="30s, 5m" {...form.register("delay")} />
              <p className="text-xs text-muted-foreground">Delay delivery after message is published</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="channels">Channels (comma-separated)</Label>
              <Input id="channels" className="font-mono text-sm" placeholder="channel-1, channel-2" {...form.register("channels")} />
            </div>

            <div className="space-y-2">
              <Label>Transformation</Label>
              <Select
                value={form.watch("transformationId") ?? ""}
                onValueChange={(v) => form.setValue("transformationId", v === "__none__" ? "" : v)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="None" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">None</SelectItem>
                  {transformations.data?.transformations.nodes.map((t) => (
                    <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">Apply a transformation to the payload before delivery</p>
            </div>

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="ordered">Ordered Delivery</Label>
                <p className="text-xs text-muted-foreground">
                  Deliver messages in order. If a delivery fails, subsequent deliveries are blocked until resolved.
                </p>
              </div>
              <Switch
                id="ordered"
                checked={form.watch("ordered") ?? false}
                onCheckedChange={(checked) => form.setValue("ordered", checked === true)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="outputFormat">Output Format</Label>
              <Select
                value={form.watch("outputFormat") ?? "default"}
                onValueChange={(v) => form.setValue("outputFormat", v as OutputFormatValue, { shouldValidate: true })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {outputFormatValues.map((v) => (
                    <SelectItem key={v} value={v}>{outputFormatLabels[v]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {!isHttp && (
            <DestinationTypeConfigFields form={form} destType={destType} errors={form.formState.errors} />
          )}

          {isHttp && !isPoll && (
            <div className="space-y-3 rounded-lg glass-card p-4">
              <Label className="text-xs font-medium">Retry Policy</Label>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label htmlFor="rp-max" className="text-xs text-muted-foreground">Max Attempts</Label>
                  <Input id="rp-max" type="number" {...form.register("retryPolicy.maxAttempts")} />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="rp-bf" className="text-xs text-muted-foreground">Backoff Factor</Label>
                  <Input id="rp-bf" type="number" step="0.1" {...form.register("retryPolicy.backoffFactor")} />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="rp-init" className="text-xs text-muted-foreground">Initial Delay</Label>
                  <Input id="rp-init" placeholder="1s" {...form.register("retryPolicy.initialDelay")} />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="rp-max-d" className="text-xs text-muted-foreground">Max Delay</Label>
                  <Input id="rp-max-d" placeholder="300s" {...form.register("retryPolicy.maxDelay")} />
                </div>
              </div>
            </div>
          )}

          {isHttp && (
            <div className="space-y-2">
              <Label htmlFor="headers">Custom Headers (JSON)</Label>
              <Textarea
                id="headers"
                className="font-mono text-xs min-h-15"
                placeholder='{"X-Custom": "value"}'
                {...form.register("headers")}
              />
              <FieldError error={form.formState.errors.headers} />
            </div>
          )}

          {isHttp && (
            <div className="space-y-3 rounded-lg glass-card p-4">
              <Label className="text-xs font-medium">Authentication</Label>
              <div className="space-y-2">
                <Label htmlFor="authType" className="text-xs text-muted-foreground">Auth Type</Label>
                <Select
                  value={authType ?? "NONE"}
                  onValueChange={(v) => form.setValue("authType", v as EndpointFormValues["authType"], { shouldValidate: true })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="NONE">None</SelectItem>
                    <SelectItem value="HEADER">Header (via Custom Headers)</SelectItem>
                    <SelectItem value="OAUTH2">OAuth2 Client Credentials</SelectItem>
                    <SelectItem value="MTLS">mTLS</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {authType === "OAUTH2" && (
                <div className="space-y-3 pt-2">
                  <div className="space-y-1">
                    <Label htmlFor="oauth2-token-url" className="text-xs text-muted-foreground">Token URL</Label>
                    <Input id="oauth2-token-url" className="font-mono text-sm" placeholder="https://auth.example.com/oauth/token" {...form.register("oauth2Config.tokenUrl")} />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label htmlFor="oauth2-client-id" className="text-xs text-muted-foreground">Client ID</Label>
                      <Input id="oauth2-client-id" {...form.register("oauth2Config.clientId")} />
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor="oauth2-client-secret" className="text-xs text-muted-foreground">Client Secret</Label>
                      <Input id="oauth2-client-secret" type="password" {...form.register("oauth2Config.clientSecret")} />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label htmlFor="oauth2-scopes" className="text-xs text-muted-foreground">Scopes</Label>
                      <Input id="oauth2-scopes" placeholder="webhooks:write" {...form.register("oauth2Config.scopes")} />
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor="oauth2-audience" className="text-xs text-muted-foreground">Audience</Label>
                      <Input id="oauth2-audience" placeholder="https://api.example.com" {...form.register("oauth2Config.audience")} />
                    </div>
                  </div>
                </div>
              )}

              {authType === "MTLS" && (
                <div className="space-y-3 pt-2">
                  <div className="space-y-1">
                    <Label htmlFor="mtls-cert" className="text-xs text-muted-foreground">Client Certificate (PEM)</Label>
                    <Textarea
                      id="mtls-cert"
                      className="font-mono text-xs min-h-20"
                      placeholder="-----BEGIN CERTIFICATE-----"
                      {...form.register("mtlsCert")}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="mtls-key" className="text-xs text-muted-foreground">Client Private Key (PEM)</Label>
                    <Textarea
                      id="mtls-key"
                      className="font-mono text-xs min-h-20"
                      placeholder="-----BEGIN PRIVATE KEY-----"
                      {...form.register("mtlsKey")}
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="autoDeletePayload">Auto Delete Payload</Label>
              <p className="text-xs text-muted-foreground">Automatically delete the event payload after successful delivery</p>
            </div>
            <Switch
              id="autoDeletePayload"
              checked={form.watch("autoDeletePayload") ?? false}
              onCheckedChange={(checked) => form.setValue("autoDeletePayload", checked === true)}
            />
          </div>

          <div className="space-y-3 rounded-lg glass-card p-4">
            <Label className="text-xs font-medium">Health Monitoring</Label>
            <p className="text-xs text-muted-foreground">
              Automatically disable the endpoint when health score drops below a threshold.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="hc-window" className="text-xs text-muted-foreground">Window (hours)</Label>
                <Input id="hc-window" type="number" placeholder="24" {...form.register("healthConfig.windowHours")} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="hc-threshold" className="text-xs text-muted-foreground">Disable Below</Label>
                <Input id="hc-threshold" type="number" step="0.01" min="0" max="1" placeholder="0.3" {...form.register("healthConfig.disableBelow")} />
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={form.formState.isSubmitting}>
              {form.formState.isSubmitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : isEdit ? "Update" : "Create"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}

function buildDefaults(initialData?: EndpointItem): EndpointFormValues {
  const destType = (initialData?.type as DestinationTypeValue) ?? "HTTP"
  const typeConfigFields = extractTypeConfigFields(destType, initialData?.typeConfig)

  return {
    applicationId: initialData?.applicationId ?? "",
    type: destType,
    url: initialData?.url ?? "",
    deliveryMode: (initialData?.deliveryMode as "PUSH" | "POLL") ?? "PUSH",
    timeoutMs: initialData?.timeoutMs ?? 30000,
    rateLimitRps: initialData?.rateLimitRps ?? 0,
    status: (initialData?.status as EndpointFormValues["status"]) ?? "ACTIVE",
    ordered: initialData?.ordered ?? false,
    outputFormat: (initialData?.outputFormat as OutputFormatValue) ?? "default",
    eventTypes: initialData?.filterConfig?.eventTypes?.join(", ") ?? "",
    retryPolicy: initialData?.retryPolicy
      ? {
          maxAttempts: initialData.retryPolicy.maxAttempts,
          initialDelay: initialData.retryPolicy.initialDelay,
          maxDelay: initialData.retryPolicy.maxDelay,
          backoffFactor: initialData.retryPolicy.backoffFactor,
        }
      : undefined,
    headers: initialData?.headers ? JSON.stringify(initialData.headers, null, 2) : "",
    authType: (initialData?.authType as EndpointFormValues["authType"]) ?? "NONE",
    oauth2Config: initialData?.oauth2Config
      ? {
          tokenUrl: initialData.oauth2Config.tokenUrl,
          clientId: initialData.oauth2Config.clientId,
          clientSecret: initialData.oauth2Config.clientSecret,
          scopes: initialData.oauth2Config.scopes ?? "",
          audience: initialData.oauth2Config.audience ?? "",
        }
      : undefined,
    mtlsCert: initialData?.mtlsCert ?? "",
    mtlsKey: initialData?.mtlsKey ?? "",
    healthConfig: initialData?.healthConfig
      ? {
          windowHours: initialData.healthConfig.windowHours,
          disableBelow: initialData.healthConfig.disableBelow,
        }
      : undefined,
    connectorType: (initialData?.connectorType as EndpointFormValues["connectorType"]) ?? "",
    autoDeletePayload: initialData?.autoDeletePayload ?? false,
    transformationId: initialData?.transformationId ?? "",
    delay: initialData?.delay ?? "",
    channels: initialData?.channels?.join(", ") ?? "",
    ...typeConfigFields,
  }
}
